import React, { useState, useEffect, useRef, useCallback, Suspense, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSearchExecution } from '../../context/SearchExecutionContext';
import { useConnectionStatus } from '../../hooks/useWebSocket';
import { fetchWithRetry } from '../../hooks/useApiWithRetry';
import { cacheManager } from '../../utils/cacheManager';
import { queryTemplatesAPI } from '../../services/api';
import { useTranslation } from 'react-i18next';
import Tooltip from '../UI/Tooltip';
import { ChevronDown, ChevronRight, Search, Filter, Download, Upload, Edit2, Edit, Trash2, Check, Copy, Trash } from 'lucide-react';
import { CardBox, Button, Badge, Input } from '../UI';
import { IconFileDownload, IconFileUpload, IconSearch, IconTrash, IconEdit, IconCheck, IconX, IconCopy, IconAlertCircle, IconRefresh } from '@tabler/icons-react';

// Lazy load modals for better performance
const CSVImportModal = lazy(() => import('./CSVImportModal'));
const RenameTemplateModal = lazy(() => import('./RenameTemplateModal'));

const QueryTemplateManager = React.memo(() => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState(new Set(['all']));
  const [selectedQueries, setSelectedQueries] = useState(new Set());
  const [showImportModal, setShowImportModal] = useState(false);
  const [showExecuteConfirm, setShowExecuteConfirm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('search_term');
  const [sortOrder, setSortOrder] = useState('asc');
  const [editingQueryId, setEditingQueryId] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [editingField, setEditingField] = useState('query'); // 'query' or 'category'
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const pollIntervalRef = useRef(null);

  // Use contexts
  const { statusColor, statusText, showReconnectButton, reconnect } = useConnectionStatus();
  const { executionState, startExecution, updateExecutionStatus, completeExecution, isExecuting } = useSearchExecution();

  const loadData = useCallback(async (retryCount = 0) => {
    try {
      setLoading(true);
      setError(null);

      // Try to load from cache first for immediate display
      const cachedTemplates = cacheManager.get('templates');
      const cachedCategories = cacheManager.get('categories');

      if (cachedTemplates && cachedCategories) {
        setTemplates(cachedTemplates);
        setCategories(cachedCategories);
        setLoading(false);
      }

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';

      // Load templates and categories in parallel with retry
      const [templatesResponse, categoriesResponse] = await Promise.all([
        fetchWithRetry(`${apiUrl}/query-templates`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        }),
        fetchWithRetry(`${apiUrl}/query-templates/categories`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        })
      ]);

      // Handle 401 errors
      if (templatesResponse.status === 401 || categoriesResponse.status === 401) {
        console.log('Authentication failed in QueryTemplateManager');
        setError(null);
        return;
      }

      if (templatesResponse.ok && categoriesResponse.ok) {
        const templatesData = await templatesResponse.json();
        const categoriesData = await categoriesResponse.json();

        const templates = templatesData.templates || [];
        const dbCategories = categoriesData.categories || [];

        // Extract unique template names as virtual categories (for AI-generated templates)
        // Only include templates that have at least one query
        const templateNameCategories = [];
        const uniqueTemplateNames = new Set();

        templates.forEach(template => {
          // If template has no categoryId but has a name and has queries, it's an AI-generated template
          const hasQueries = template.queries && Array.isArray(template.queries) && template.queries.length > 0;
          if (!template.categoryId && template.name && !uniqueTemplateNames.has(template.name) && hasQueries) {
            uniqueTemplateNames.add(template.name);
            templateNameCategories.push({
              id: `template-${template.name}`, // Virtual ID
              name: template.name,
              isVirtual: true, // Mark as virtual for filtering
              color: template.categoryColor || '#3B82F6',
              templateCount: 0 // Will be calculated in UI
            });
          }
        });

        // Merge database categories with virtual template-based categories
        const allCategories = [...dbCategories, ...templateNameCategories];

        // Update state
        setTemplates(templates);
        setCategories(allCategories);
        setError(null);

        // Cache the data
        cacheManager.set('templates', templates);
        cacheManager.set('categories', allCategories);
      } else {
        throw new Error('Failed to load data');
      }
    } catch (error) {
      console.error('Failed to load templates:', error);

      // If we have cached data and network failed, keep using cached data
      const cachedTemplates = cacheManager.get('templates');
      const cachedCategories = cacheManager.get('categories');

      if (cachedTemplates && cachedCategories) {
        setTemplates(cachedTemplates);
        setCategories(cachedCategories);
        setError(null);
      } else if (retryCount < 1 && !error.message?.includes('401')) {
        setTimeout(() => {
          loadData(retryCount + 1);
        }, 2000);
      } else if (!error.message?.includes('401')) {
        setError('Failed to load query templates. Please refresh the page.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && localStorage.getItem('accessToken')) {
      loadData();
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [user, loadData]);

  // Resume polling if there's an active search
  useEffect(() => {
    if (executionState.isRunning && executionState.batchId && !pollIntervalRef.current) {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';

      pollIntervalRef.current = setInterval(async () => {
        try {
          const response = await fetchWithRetry(`${apiUrl}/search-execution/batches/${executionState.batchId}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
            }
          });

          if (response.ok) {
            const batchData = await response.json();
            console.log('📊 Resuming batch status polling:', batchData.batch.status);

            if (batchData.batch.status === 'completed') {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
              completeExecution('completed');
              navigate(`/results/${executionState.batchId}`);
            } else if (batchData.batch.status === 'failed') {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
              completeExecution('failed');
              alert(t('queryTemplates.executionFailed'));
            }
          }
        } catch (error) {
          console.error('Error polling batch status:', error);
        }
      }, 3000);
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [executionState.isRunning, executionState.batchId, navigate, completeExecution]);

  const handleCategoryToggle = (categoryId) => {
    const newSelected = new Set(selectedCategories);

    if (categoryId === 'all') {
      if (newSelected.has('all')) {
        newSelected.clear();
      } else {
        newSelected.clear();
        newSelected.add('all');
      }
    } else {
      // Remove 'all' if selecting specific category
      newSelected.delete('all');

      if (newSelected.has(categoryId)) {
        newSelected.delete(categoryId);
      } else {
        newSelected.add(categoryId);
      }

      // If no categories selected, select 'all'
      if (newSelected.size === 0) {
        newSelected.add('all');
      }
    }

    setSelectedCategories(newSelected);
    // Clear query selection when changing categories
    setSelectedQueries(new Set());
  };

  const handleQuerySelection = (queryId) => {
    const newSelected = new Set(selectedQueries);
    if (newSelected.has(queryId)) {
      newSelected.delete(queryId);
    } else {
      newSelected.add(queryId);
    }
    setSelectedQueries(newSelected);
  };

  // Flatten all queries from templates into a single array with metadata
  const getAllQueries = useCallback(() => {
    const allQueries = [];

    templates.forEach(template => {
      if (template.queries && Array.isArray(template.queries)) {
        template.queries.forEach((query, index) => {
          allQueries.push({
            id: `${template.id}_${index}`, // Unique ID for each query
            templateId: template.id,
            templateName: template.name,
            categoryId: template.categoryId,
            categoryName: template.categoryName,
            categoryColor: template.categoryColor,
            search_term: query.search_term || template.searchTerm,
            location: query.location || template.location,
            device: query.device || template.device || 'desktop',
            language: query.language || template.language || 'en',
            search_engine: query.search_engine || template.search_engine || 'google',
            tags: query.tags || template.tags || [],
            priority: query.priority || template.priority || 0,
            createdAt: template.createdAt,
            updatedAt: template.updatedAt
          });
        });
      } else {
        // If no queries array, treat the template itself as a single query
        allQueries.push({
          id: `${template.id}_0`,
          templateId: template.id,
          templateName: template.name,
          categoryId: template.categoryId,
          categoryName: template.categoryName,
          categoryColor: template.categoryColor,
          search_term: template.searchTerm,
          location: template.location,
          device: template.device || 'desktop',
          language: template.language || 'en',
          search_engine: template.search_engine || 'google',
          tags: template.tags || [],
          priority: template.priority || 0,
          createdAt: template.createdAt,
          updatedAt: template.updatedAt
        });
      }
    });

    return allQueries;
  }, [templates]);

  const getFilteredQueries = useCallback(() => {
    let queries = getAllQueries();

    // Filter by categories (handle both real and virtual categories)
    if (!selectedCategories.has('all')) {
      queries = queries.filter(q => {
        // Check if query matches a real category (by categoryId)
        if (q.categoryId && selectedCategories.has(q.categoryId)) {
          return true;
        }

        // Check if query matches a virtual category (by template name)
        // Virtual category IDs are in format: template-${templateName}
        const virtualCategoryId = `template-${q.templateName}`;
        if (selectedCategories.has(virtualCategoryId)) {
          return true;
        }

        return false;
      });
    }

    // Filter by search - include both templateName and categoryName
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      queries = queries.filter(q =>
        q.search_term.toLowerCase().includes(query) ||
        (q.templateName && q.templateName.toLowerCase().includes(query)) ||
        (q.categoryName && q.categoryName.toLowerCase().includes(query))
      );
    }

    // Sort
    queries.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      if (sortBy === 'createdAt' || sortBy === 'updatedAt') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return queries;
  }, [selectedCategories, getAllQueries, searchQuery, sortBy, sortOrder]);

  const handleSelectAll = useCallback(() => {
    const filteredQueries = getFilteredQueries();
    if (selectedQueries.size === filteredQueries.length) {
      setSelectedQueries(new Set());
    } else {
      setSelectedQueries(new Set(filteredQueries.map(q => q.id)));
    }
  }, [getFilteredQueries, selectedQueries]);

  const executeSelectedQueries = async () => {
    if (selectedQueries.size === 0 || isExecuting()) {
      return;
    }

    // Show confirmation modal instead of executing immediately
    setShowExecuteConfirm(true);
  };

  const confirmExecute = async () => {
    setShowExecuteConfirm(false);

    const allQueries = getAllQueries();
    const selectedQueriesList = allQueries.filter(q => selectedQueries.has(q.id));

    // Group selected queries by templateId to execute by template
    const templateIds = new Set(selectedQueriesList.map(q => q.templateId));

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';

      const response = await fetchWithRetry(`${apiUrl}/search-execution/batches?execute=true`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        },
        body: JSON.stringify({
          name: `Search Execution - ${new Date().toLocaleDateString(i18n.language === 'he' ? 'he-IL' : 'en-US')}`,
          template_ids: Array.from(templateIds)
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Batch created successfully:', data.batch.id);

        startExecution(data.batch.id, data.batch.name || `Search Execution - ${new Date().toLocaleDateString(i18n.language === 'he' ? 'he-IL' : 'en-US')}`);
        setSelectedQueries(new Set());

        pollIntervalRef.current = setInterval(async () => {
          try {
            const statusResponse = await fetchWithRetry(`${apiUrl}/search-execution/batches/${data.batch.id}`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
              }
            });

            if (statusResponse.ok) {
              const batchData = await statusResponse.json();
              console.log('📊 Batch status:', batchData.batch.status);

              if (batchData.batch.status === 'completed') {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
                completeExecution('completed');
                navigate(`/results/${data.batch.id}`);
              } else if (batchData.batch.status === 'failed') {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
                completeExecution('failed');
                alert(t('queryTemplates.executionFailedRetry'));
              }
            }
          } catch (error) {
            console.error('Error polling batch status:', error);
          }
        }, 3000);

      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to start execution');
      }
    } catch (error) {
      console.error('Failed to execute templates:', error);
      completeExecution('failed');
      alert(error.message || t('queryTemplates.executionError'));
    }
  };

  const downloadSampleCSV = async () => {
    const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5555';

    try {
      const response = await fetch(`${baseUrl}/sample-csv`);
      const csvText = await response.text();

      const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.download = 'sample-query-templates.csv';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading sample CSV:', error);
      alert(t('queryTemplates.downloadFailed'));
    }
  };

  const exportQueriesToCSV = () => {
    const queries = getFilteredQueries();

    // Create CSV header - match the sample CSV format
    const headers = ['search_term', 'category'];

    // Create CSV rows - use categoryName as category
    const rows = queries.map(q => [
      q.search_term,
      q.categoryName || ''
    ]);

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = `queries_export_${new Date().toISOString().slice(0, 10)}.csv`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Cleanup
    URL.revokeObjectURL(url);
  };

  const handleImportComplete = async () => {
    cacheManager.remove('templates');
    cacheManager.remove('categories');
    await loadData();
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const [copiedQueryId, setCopiedQueryId] = useState(null);

  const copyToClipboard = (text, queryId) => {
    navigator.clipboard.writeText(text);
    setCopiedQueryId(queryId);
    setTimeout(() => setCopiedQueryId(null), 2000);
  };

  const handleEditQuery = (query, field = 'query') => {
    setEditingQueryId(query.id);
    setEditingField(field);

    if (field === 'query') {
      setEditingValue(query.search_term);
    } else if (field === 'category') {
      setSelectedCategoryId(query.categoryId || '');
    }
  };

  const handleSaveEdit = async (query) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';

      let updatePayload = {};

      if (editingField === 'query') {
        console.log('Saving query edit:', {
          queryId: query.id,
          templateId: query.templateId,
          queryIndex: query.id.split('_')[1],
          newValue: editingValue
        });

        // Get the current template to update the queries array
        const template = templates.find(t => t.id === query.templateId);
        if (!template) {
          throw new Error('Template not found');
        }

        // Update the specific query in the queries array
        const queryIndex = parseInt(query.id.split('_')[1]);
        const updatedQueries = [...(template.queries || [])];

        if (updatedQueries[queryIndex]) {
          updatedQueries[queryIndex] = {
            ...updatedQueries[queryIndex],
            search_term: editingValue
          };
        }

        updatePayload = { queries: updatedQueries };

      } else if (editingField === 'category') {
        console.log('Saving category edit:', {
          templateId: query.templateId,
          newCategoryId: selectedCategoryId
        });

        updatePayload = { category_id: selectedCategoryId || null };
      }

      // Update the template
      const response = await fetchWithRetry(`${apiUrl}/query-templates/${query.templateId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        },
        body: JSON.stringify(updatePayload)
      });

      console.log('Response received:', {
        ok: response.ok,
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
        type: response.type
      });

      // Try to get response body for debugging
      const responseText = await response.text();
      console.log('Response body text:', responseText);

      let responseData;
      try {
        responseData = JSON.parse(responseText);
        console.log('Parsed response data:', responseData);
      } catch (e) {
        console.log('Could not parse response as JSON');
        responseData = null;
      }

      if (response.ok && responseData) {
        console.log('Update successful! Updating local state...');
        // Update local state
        setTemplates(prevTemplates =>
          prevTemplates.map(template => {
            if (template.id === query.templateId) {
              return {
                ...template,
                search_term: editingValue,  // Update template's search_term
                searchTerm: editingValue,   // Keep both for compatibility
                queries: template.queries?.map((q, index) =>
                  `${template.id}_${index}` === query.id
                    ? { ...q, search_term: editingValue }
                    : q
                )
              };
            }
            return template;
          })
        );

        // Clear cache to ensure fresh data on next load
        cacheManager.remove('templates');
        cacheManager.remove('categories');

        setEditingQueryId(null);
        setEditingValue('');
        setSelectedCategoryId(null);
        setEditingField('query');

        console.log('Update completed successfully!');
        // Reload data to ensure sync
        await loadData();
      } else {
        console.log('Response not ok OR no response data, reading error:', {
          responseOk: response.ok,
          hasResponseData: !!responseData,
          status: response.status,
          statusText: response.statusText
        });

        let errorMessage = 'Unknown error';

        if (responseData && responseData.error) {
          errorMessage = responseData.error;
        } else if (responseText) {
          errorMessage = `Server response: ${responseText}`;
        }

        console.log('Error message to show:', errorMessage);
        alert(t('queryTemplates.updateFailed', { error: errorMessage }));
      }
    } catch (error) {
      console.error('Error updating query - caught exception:', error);
      alert(t('queryTemplates.updateError'));
    }
  };

  const handleCancelEdit = () => {
    setEditingQueryId(null);
    setEditingValue('');
    setSelectedCategoryId(null);
    setEditingField('query');
  };

  const handleDeleteQuery = async (query) => {
    const confirmMessage = `Are you sure you want to delete this query?\n\n"${query.search_term}"`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';

      // Get the current template
      const template = templates.find(t => t.id === query.templateId);
      if (!template) {
        throw new Error('Template not found');
      }

      // Remove the specific query from the queries array
      const queryIndex = parseInt(query.id.split('_')[1]);
      const updatedQueries = [...(template.queries || [])];
      updatedQueries.splice(queryIndex, 1);

      // Update the template with the modified queries array
      const response = await fetchWithRetry(`${apiUrl}/query-templates/${query.templateId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        },
        body: JSON.stringify({
          queries: updatedQueries
        })
      });

      if (response.ok) {
        // Clear cache and reload
        cacheManager.remove('templates');
        cacheManager.remove('categories');
        await loadData();
      } else {
        const error = await response.json();
        alert(t('queryTemplates.deleteFailed', { error: error.error || t('common.unknownError') }));
      }
    } catch (error) {
      console.error('Error deleting query:', error);
      alert(t('queryTemplates.deleteError'));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedQueries.size === 0) {
      return;
    }

    const confirmMessage = `Are you sure you want to delete ${selectedQueries.size} selected queries?\n\nThis action cannot be undone.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';
      const allQueries = getAllQueries();

      // Group selected queries by template
      const templateUpdates = new Map();

      allQueries.forEach(q => {
        if (selectedQueries.has(q.id)) {
          if (!templateUpdates.has(q.templateId)) {
            templateUpdates.set(q.templateId, []);
          }
          templateUpdates.get(q.templateId).push(q.id);
        }
      });

      // Update each template
      for (const [templateId, queryIdsToDelete] of templateUpdates) {
        const template = templates.find(t => t.id === templateId);
        if (!template) continue;

        // Filter out the queries to delete
        const updatedQueries = (template.queries || []).filter((_, index) => {
          const queryId = `${templateId}_${index}`;
          return !queryIdsToDelete.includes(queryId);
        });

        // Update the template
        await fetchWithRetry(`${apiUrl}/query-templates/${templateId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          },
          body: JSON.stringify({
            queries: updatedQueries
          })
        });
      }

      // Clear selection and reload
      setSelectedQueries(new Set());
      cacheManager.remove('templates');
      cacheManager.remove('categories');
      await loadData();

      alert(t('queryTemplates.deleteSuccess', { count: selectedQueries.size }));
    } catch (error) {
      console.error('Error deleting selected queries:', error);
      alert(t('queryTemplates.deleteSelectedError'));
    }
  };

  const filteredQueries = getFilteredQueries();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin' || user?.isImpersonation;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <CardBox className="text-center max-w-sm">
          <svg className="animate-spin h-12 w-12 text-primary mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-600 dark:text-[#c4c4c4]">{t('queryTemplates.loading')}</p>
        </CardBox>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <CardBox className="text-center max-w-md">
          <IconAlertCircle size={48} className="mx-auto text-red-500 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-[#f5f5f5] mb-2">{t('queryTemplates.errorLoading')}</h3>
          <p className="text-gray-500 dark:text-[#999999] mb-4">{error}</p>
          <div className="flex gap-3 justify-center">
            <Button
              onClick={() => loadData()}
              leftIcon={<IconRefresh size={18} />}
            >
              {t('common.tryAgain')}
            </Button>
            <Button
              onClick={() => window.location.reload()}
              variant="ghost"
            >
              {t('common.refreshPage')}
            </Button>
          </div>
        </CardBox>
      </div>
    );
  }

  return (
    <div className="h-full flex p-6 gap-6">
      {/* Categories Sidebar */}
      <CardBox className="w-80 flex-shrink-0 p-0 overflow-hidden">
        <div className="p-5 border-b border-gray-100 dark:border-[#2e2e2e]">
          <h3 className="font-semibold text-gray-900 dark:text-[#f5f5f5] text-lg">{t('queryTemplates.categories')}</h3>
          <p className="text-xs text-gray-600 dark:text-[#c4c4c4] mt-2">{t('queryTemplates.filterByCategory')}</p>
        </div>

        <div className="p-3 space-y-1 max-h-[calc(100vh-200px)] overflow-y-auto">
          {/* All Categories */}
          <label className="flex items-center p-3 hover:bg-gray-50 dark:hover:bg-[#262626] rounded-lg cursor-pointer group transition-all">
            <input
              type="checkbox"
              checked={selectedCategories.has('all')}
              onChange={() => handleCategoryToggle('all')}
              className="w-4 h-4 text-green-600 border-gray-200 dark:border-[#2e2e2e] rounded focus:ring-green-500 focus:ring-2"
            />
            <span className="ml-3 text-sm font-medium text-gray-700 dark:text-[#c4c4c4] group-hover:text-green-600 transition-colors flex-1">
              {t('queryTemplates.allCategories')}
            </span>
            <span className="ml-2 text-xs text-gray-600 dark:text-[#c4c4c4] bg-gray-100 dark:bg-[#262626] px-2 py-1 rounded-lg">
              {getAllQueries().length}
            </span>
          </label>

          <div className="my-3 h-px bg-gray-100 dark:bg-[#262626]"></div>

          {/* Individual Categories */}
          {categories.map(category => {
            // Calculate query count based on whether it's a real or virtual category
            const categoryQueries = getAllQueries().filter(q => {
              if (category.isVirtual) {
                // Virtual category: match by template name
                return q.templateName === category.name;
              } else {
                // Real category: match by categoryId
                return q.categoryId === category.id;
              }
            });

            return (
              <label key={category.id} className="flex items-center p-3 hover:bg-gray-50 dark:hover:bg-[#262626] rounded-lg cursor-pointer group transition-all">
                <input
                  type="checkbox"
                  checked={selectedCategories.has(category.id)}
                  onChange={() => handleCategoryToggle(category.id)}
                  className="w-4 h-4 text-green-600 border-gray-200 dark:border-[#2e2e2e] rounded focus:ring-green-500 focus:ring-2"
                />
                <span className="ml-3 text-sm text-gray-700 dark:text-[#c4c4c4] flex-1 group-hover:text-green-600 transition-colors">
                  {category.name}
                </span>
                <span className="ml-2 text-xs text-gray-600 dark:text-[#c4c4c4] bg-gray-100 dark:bg-[#262626] px-2 py-1 rounded-lg">
                  {categoryQueries.length}
                </span>
              </label>
            );
          })}

          {categories.length === 0 && (
            <div className="text-center py-8">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 dark:bg-[#262626] flex items-center justify-center">
                <Filter className="w-6 h-6 text-gray-400 dark:text-[#999999]" />
              </div>
              <p className="text-sm text-gray-500 dark:text-[#999999] italic">
                {t('queryTemplates.noCategories')}
              </p>
            </div>
          )}
        </div>
      </CardBox>

      {/* Main Content Area */}
      <CardBox className="flex-1 flex flex-col min-w-0 overflow-hidden p-0">
        {/* Global Search Status Notification */}
        {executionState.isRunning && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-t-xl border-b border-yellow-100 dark:border-yellow-700/40 px-6 py-4">
            <div className="flex items-center gap-3">
              <svg className="animate-spin h-5 w-5 text-yellow-600 dark:text-yellow-200" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <div>
                <p className="font-medium text-yellow-700 dark:text-yellow-200 text-sm">{t('queryTemplates.searchInProgress')}</p>
                <p className="text-xs text-yellow-600 dark:text-yellow-200">
                  You will be notified by email when the results are ready.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Header with Actions */}
        <div className="px-6 py-5 border-b border-gray-100 dark:border-[#2e2e2e]">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-[#f5f5f5] heading-mono">{t('queryTemplates.title')}</h1>
              <p className="text-sm text-gray-600 dark:text-[#c4c4c4] mt-2">
                {t('queryTemplates.subtitle')}
              </p>
            </div>

            <div className="flex items-center gap-3">
              <Button
                onClick={downloadSampleCSV}
                variant="ghost"
                leftIcon={<IconFileDownload size={18} />}
              >
                {t('queryTemplates.sampleCSV')}
              </Button>

              {isAdmin && (
                <Button
                  onClick={exportQueriesToCSV}
                  leftIcon={<IconFileDownload size={18} />}
                >
                  {t('queryTemplates.exportQueries')}
                </Button>
              )}

              <Button
                onClick={() => setShowImportModal(true)}
                leftIcon={<IconFileUpload size={18} />}
              >
                {t('queryTemplates.importCSV')}
              </Button>
            </div>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="px-6 py-4 border-b border-gray-100 dark:border-[#2e2e2e]">
          <div className="flex items-center gap-4">
            {/* Search Input - Always visible, optimal width */}
            <div className="relative w-64 flex items-center">
              <div className="absolute left-3 h-9 w-9 flex items-center justify-center pointer-events-none">
                <Search className="w-4 h-4 text-gray-400 dark:text-[#999999]" />
              </div>
              <input
                type="text"
                placeholder={t('queryTemplates.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-gray-50 dark:bg-[#262626] pl-10 pr-4 py-2 w-full rounded-lg text-sm focus:ring-2 focus:ring-green-500/20 focus:bg-white dark:focus:bg-[#1f1f1f] focus:outline-none transition-all"
              />
            </div>

            {/* Selection Actions - Center section with flex-1 */}
            <div className="flex items-center gap-3 flex-1">
              <button
                onClick={handleSelectAll}
                className="text-green-600 hover:text-green-700 text-sm font-medium transition-colors whitespace-nowrap"
              >
                {selectedQueries.size === filteredQueries.length ? t('queryTemplates.deselectAll') : t('queryTemplates.selectAll')}
              </button>

              <div className="bg-gray-100 dark:bg-[#262626] px-3 py-1 rounded-lg text-xs font-medium text-gray-700 dark:text-[#c4c4c4] whitespace-nowrap">
                {t('queryTemplates.selected', { count: selectedQueries.size })}
              </div>

              {isAdmin && selectedQueries.size > 0 && (
                <button
                  onClick={handleDeleteSelected}
                  className="text-red-600 hover:text-red-700 text-sm font-medium flex items-center gap-1 transition-colors whitespace-nowrap"
                >
                  <Trash className="w-4 h-4" />
                  Delete Selected
                </button>
              )}
            </div>

            {/* Execute Button - Compact width */}
            {selectedQueries.size > 0 ? (
              <Button
                onClick={executeSelectedQueries}
                disabled={isExecuting()}
                loading={executionState.isRunning}
              >
                {executionState.isRunning ? 'Processing...' : t('queryTemplates.execute')}
              </Button>
            ) : (
              /* Placeholder to maintain layout when no queries selected */
              <div className="w-24"></div>
            )}
          </div>
        </div>

        {/* Table Content */}
        <div className="flex-1 relative bg-gray-50 dark:bg-[#262626]">
          {filteredQueries.length === 0 ? (
            // Empty State
            <div className="flex items-center justify-center h-full">
              <div className="text-center p-8">
                <div className="bg-white dark:bg-[#1f1f1f] w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm">
                  <svg className="w-10 h-10 text-gray-400 dark:text-[#999999]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-[#f5f5f5] mb-3 heading-mono">
                  {searchQuery ? t('queryTemplates.noQueriesFound') : t('queryTemplates.noQueriesYet')}
                </h3>
                <p className="text-gray-600 dark:text-[#c4c4c4] mb-8 max-w-md">
                  {searchQuery
                    ? t('queryTemplates.tryAdjustingFilters')
                    : t('queryTemplates.importToGetStarted')}
                </p>
                {!searchQuery && (
                  <div className="flex gap-3 justify-center">
                    <Button
                      onClick={downloadSampleCSV}
                      variant="ghost"
                      leftIcon={<IconFileDownload size={18} />}
                    >
                      Download Sample
                    </Button>
                    <Button
                      onClick={() => setShowImportModal(true)}
                      leftIcon={<IconFileUpload size={18} />}
                    >
                      Import Templates
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            // Table with horizontal and vertical scroll
            <div className="absolute inset-0 overflow-auto">
              <table className="w-full" style={{minWidth: '1400px'}}>
              <thead className="bg-white dark:bg-[#1f1f1f] border-b border-gray-200 dark:border-[#2e2e2e] sticky top-0 z-10 shadow-sm">
                <tr>
                  <th className="w-12 px-4 py-4" style={{minWidth: '48px'}}>
                    <input
                      type="checkbox"
                      checked={selectedQueries.size === filteredQueries.length && filteredQueries.length > 0}
                      onChange={handleSelectAll}
                      className="w-4 h-4 text-green-600 border-gray-200 dark:border-[#2e2e2e] rounded focus:ring-green-500 focus:ring-2"
                    />
                  </th>
                  <th
                    className="px-6 py-4 text-left text-xs font-medium text-gray-600 dark:text-[#c4c4c4] uppercase tracking-wider cursor-pointer hover:text-green-600 transition-colors"
                    style={{minWidth: '400px'}}
                    onClick={() => handleSort('search_term')}
                  >
                    <div className="flex items-center gap-2">
                      {t('queryTemplates.query')}
                      {sortBy === 'search_term' && (
                        <span className="text-green-600 text-sm">
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-600 dark:text-[#c4c4c4] uppercase tracking-wider" style={{minWidth: '150px'}}>
                    {t('queryTemplates.category')}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-600 dark:text-[#c4c4c4] uppercase tracking-wider" style={{minWidth: '120px'}}>
                    {t('queryTemplates.location')}
                  </th>
                  {isAdmin && (
                    <th className="px-6 py-4 text-center text-xs font-medium text-gray-600 dark:text-[#c4c4c4] uppercase tracking-wider" style={{minWidth: '100px'}}>
                      {t('queryTemplates.actions')}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-[#2e2e2e] bg-white dark:bg-[#1f1f1f]">
                {filteredQueries.map((query) => {
                  const isSelected = selectedQueries.has(query.id);

                  return (
                    <tr
                      key={query.id}
                      className={`transition-all ${isSelected ? 'bg-green-50 dark:bg-green-900/20' : 'hover:bg-gray-50 dark:hover:bg-[#262626]'}`}
                    >
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleQuerySelection(query.id)}
                          className="w-4 h-4 text-green-600 border-gray-200 dark:border-[#2e2e2e] rounded focus:ring-green-500 focus:ring-2"
                        />
                      </td>
                      <td className="px-6 py-4">
                        {editingQueryId === query.id ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleSaveEdit(query);
                                } else if (e.key === 'Escape') {
                                  handleCancelEdit();
                                }
                              }}
                              className="flex-1 bg-white dark:bg-[#1f1f1f] border border-gray-300 dark:border-[#2e2e2e] px-3 py-2 text-sm rounded-lg focus:ring-2 focus:ring-green-500/20 focus:outline-none"
                              autoFocus
                            />
                            <button
                              onClick={() => handleSaveEdit(query)}
                              className="text-green-600 hover:text-green-700 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-[#262626] transition-all"
                              title="Save"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="text-red-400 hover:text-red-300 p-1 rounded-lg glass-interactive transition-all duration-200"
                              title="Cancel"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ) : (
                          <div
                            className="text-sm font-medium text-primary whitespace-nowrap cursor-pointer hover:text-gray-600 dark:hover:text-[#c4c4c4] transition-colors relative group text-mono"
                            onClick={() => copyToClipboard(query.search_term, query.id)}
                            title="Click to copy"
                          >
                            {query.search_term}
                            {copiedQueryId === query.id && (
                              <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 glass-badge px-2 py-1 rounded-lg text-xs animate-fade-in-scale">
                                Copied!
                              </span>
                            )}
                            <Copy className="w-3 h-3 inline-block ml-2 opacity-0 group-hover:opacity-60 transition-opacity" />
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {editingQueryId === query.id && editingField === 'category' ? (
                          <div className="flex items-center gap-2">
                            <select
                              value={selectedCategoryId}
                              onChange={(e) => setSelectedCategoryId(e.target.value)}
                              className="flex-1 glass-select px-3 py-2 text-sm rounded-lg focus:ring-2 focus:ring-accent-400/50"
                              autoFocus
                            >
                              <option value="">{t('queryTemplates.noCategory')}</option>
                              {categories.map(cat => (
                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                              ))}
                            </select>
                            <button
                              onClick={() => handleSaveEdit(query)}
                              className="text-green-600 hover:text-green-700 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-[#262626] transition-all"
                              title="Save"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="text-red-400 hover:text-red-300 p-1 rounded-lg glass-interactive transition-all duration-200"
                              title="Cancel"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ) : (
                          <div
                            className={`inline-flex items-center glass-badge ${isAdmin ? 'cursor-pointer' : ''}`}
                            style={{
                              backgroundColor: query.categoryColor ? `${query.categoryColor}20` : 'rgba(255, 255, 255, 0.1)',
                              borderColor: query.categoryColor ? `${query.categoryColor}40` : 'rgba(255, 255, 255, 0.2)',
                              color: query.categoryColor || 'rgba(255, 255, 255, 0.8)'
                            }}
                            onClick={() => isAdmin && handleEditQuery(query, 'category')}
                            title={isAdmin ? 'Click to edit category' : ''}
                          >
                            {query.categoryName || 'Uncategorized'}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-secondary text-mono">
                        {query.location || '-'}
                      </td>
                      {isAdmin && (
                        <td className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => handleEditQuery(query)}
                              className="text-accent-400 hover:text-accent-300 p-2 rounded-lg glass-interactive transition-all duration-200 hover:shadow-lg"
                              title="Edit query"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteQuery(query)}
                              className="text-red-400 hover:text-red-300 p-2 rounded-lg glass-interactive transition-all duration-200 hover:shadow-lg"
                              title="Delete query"
                            >
                              <Trash className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Connection Status */}
        {showReconnectButton && (
          <div className="bg-red-50 dark:bg-red-900/20 rounded-b-xl border-t border-red-100 dark:border-red-700/40 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${statusColor} animate-pulse`}></div>
                <span className="text-sm text-red-600 dark:text-red-200 font-medium">
                  {statusText} - Real-time updates unavailable
                </span>
              </div>
              <Button
                onClick={reconnect}
                size="sm"
                variant="ghost"
                className="text-red-600 dark:text-red-200"
              >
                Reconnect
              </Button>
            </div>
          </div>
        )}
      </CardBox>

      {/* Modals */}
      <Suspense fallback={null}>
        {showImportModal && (
          <CSVImportModal
            isOpen={showImportModal}
            onClose={() => setShowImportModal(false)}
            onImportComplete={handleImportComplete}
          />
        )}
      </Suspense>

      {/* Execute Confirmation Modal */}
      {showExecuteConfirm && (() => {
        const allQueries = getAllQueries();
        const selectedQueriesList = allQueries.filter(q => selectedQueries.has(q.id));
        const templateIds = new Set(selectedQueriesList.map(q => q.templateId));

        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-[#1f1f1f] rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30">
                  <IconSearch size={24} className="text-green-600 dark:text-green-200" />
                </div>
                <h3 className="text-xl font-semibold dark:text-[#f5f5f5]">{t('queryTemplates.executeConfirmTitle', 'Ready to Execute Search Analysis')}</h3>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-[#2e2e2e]">
                  <span className="text-gray-600 dark:text-[#c4c4c4]">{t('queryTemplates.queries', 'Queries')}:</span>
                  <span className="font-semibold dark:text-[#f5f5f5]">{selectedQueries.size}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-[#2e2e2e]">
                  <span className="text-gray-600 dark:text-[#c4c4c4]">{t('queryTemplates.groups', 'From groups')}:</span>
                  <span className="font-semibold dark:text-[#f5f5f5]">{templateIds.size}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-[#2e2e2e]">
                  <span className="text-gray-600 dark:text-[#c4c4c4]">{t('queryTemplates.engines', 'Search Engines')}:</span>
                  <span className="font-semibold text-sm dark:text-[#f5f5f5]">ChatGPT, Gemini, Perplexity</span>
                </div>
              </div>

              <p className="text-gray-600 dark:text-[#c4c4c4] text-sm mb-6">
                {t('queryTemplates.executeConfirmMessage', 'This will run all selected queries across all AI search engines to discover and analyze your competitors.')}
              </p>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowExecuteConfirm(false)}
                  className="px-4 py-2 text-gray-700 dark:text-[#c4c4c4] hover:bg-gray-100 dark:hover:bg-[#262626] rounded-lg transition-colors"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
                <button
                  onClick={confirmExecute}
                  className="px-4 py-2 rounded-lg transition-colors text-gray-800 font-medium"
                  style={{ backgroundColor: '#d8f9b8' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#c5e6a5'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#d8f9b8'}
                >
                  {t('queryTemplates.executeButton', 'Execute')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
});

QueryTemplateManager.displayName = 'QueryTemplateManager';

export default QueryTemplateManager;